import {
  AppNavigationProvider,
  GTagProvider,
  GTagProviderProps,
  Loader,
  loaderStylesSpinnerXLarge,
  setupGtm,
} from '@h2oai/ui-kit';
import React from 'react';
import TagManager from 'react-gtm-module';
import { useAuth } from 'react-oidc-context';

import { AutoLogout } from '../../components/AutoLogout/AutoLogout';
import { getAppStoreMenuInfo } from '../../components/Navigation/utils';
import { useQueryEnvironment } from '../../discovery-service/discoveryService.hooks';
import { useQueryAppstoreUser, useQueryCookieSession, useQueryEnvironmentData } from '../../services/appstore.hooks';
import { EnvironmentAndMenu, EnvProvider, UserProvider } from '../../utils/contexts';
import { useCloudPlatformDiscovery, useDynamicFavicon } from '../../utils/hooks';
import { Protected } from '../Protected/Protected';

function AuthPage() {
  const auth = useAuth();

  const platformDiscovery = useCloudPlatformDiscovery();
  const [gTag, setGTag] = React.useState<GTagProviderProps>({ isGtmReady: false, user: {} });
  const environmentQuery = useQueryEnvironment({
    useErrorBoundary: true,
  });
  const environment = environmentQuery.data;
  const environmentDataQuery = useQueryEnvironmentData({
    enabled: !auth.isLoading,
  });
  const environmentData = environmentDataQuery.data;
  const appstoreUserQuery = useQueryAppstoreUser({
    enabled: !auth.isLoading,
  });
  const user = appstoreUserQuery.data;

  React.useEffect(() => {
    if (!appstoreUserQuery.isFetched) {
      return;
    }
    if (!user) {
      auth.signinRedirect();
      return;
    }
    if (!environmentDataQuery.isFetched) {
      return;
    }
    if (!environmentData) {
      throw environmentDataQuery.error ?? new Error('Failed to fetch environment data');
    }
    if (!environmentData.publicModeEnabled && !auth.isAuthenticated) {
      auth.signinRedirect();
    }
  }, [
    appstoreUserQuery.isFetched,
    auth.isAuthenticated,
    environmentData,
    environmentDataQuery.error,
    environmentDataQuery.isFetched,
    user,
  ]);

  const env = React.useMemo<EnvironmentAndMenu | undefined>(() => {
    if (!environment || !environmentData || !user) {
      return undefined;
    }
    const { navLinks = [], bookTimeLink, homePage, federationEnabled, environmentName } = environmentData;

    const driveEnabled =
      !!platformDiscovery?.stsDriveUrl && !!platformDiscovery?.userDriveUrl && environmentData?.h2oDriveEnabled;

    return {
      ...environmentData,
      h2oCloudVersion: environment.h2oCloudVersion || 'Unknown version',
      menu: getAppStoreMenuInfo(
        !!platformDiscovery?.aiEngineManagerApiUrl,
        user,
        navLinks,
        bookTimeLink,
        homePage.enabled,
        federationEnabled,
        '',
        true,
        undefined,
        environmentName,
        environmentData.platformUsageEnabled,
        environmentData.publicModeEnabled && !user.hasFullAccess,
        !!platformDiscovery?.notebookApiUrl,
        !!platformDiscovery?.secureStore,
        environmentData.cliDownloadEnabled,
        environmentData.adminCenterEnabled,
        !!platformDiscovery?.auditTrailApiUrl,
        driveEnabled
      ),
    };
  }, [environment, environmentData, platformDiscovery, user]);

  useDynamicFavicon(environmentData?.logoUrl || '/favicon.ico');

  React.useEffect(() => {
    if (env?.tagManagerId && user?.id) {
      setupGtm(TagManager, env.tagManagerId, user, env.h2oCloudVersion).then(setGTag, (error) => {
        console.error('Google tag manager initialization failed');
        console.error(error);
      });
    }
  }, [env?.tagManagerId, env?.h2oCloudVersion, user?.id]);

  const cookieSessionQuery = useQueryCookieSession(auth.user?.access_token);
  const dataReady = user && env;
  const isAuthenticated = auth.user?.access_token && cookieSessionQuery.isSuccess;
  const showApp = dataReady && (isAuthenticated || env?.publicModeEnabled);

  return showApp ? (
    <GTagProvider {...gTag}>
      <EnvProvider env={env}>
        <UserProvider user={user}>
          {auth.user && <AutoLogout />}
          <AppNavigationProvider>
            <Protected />
          </AppNavigationProvider>
        </UserProvider>
      </EnvProvider>
    </GTagProvider>
  ) : (
    <Loader styles={loaderStylesSpinnerXLarge} label="Loading..." />
  );
}

export default AuthPage;
